import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { Loader2, AlertTriangle, MessageSquare, CheckCircle, User, Bell, RefreshCw } from 'lucide-react';
import { notificationService, NotificationsParams } from '../../services/notificationService';
import { showToast } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { ProLockOverlay } from '../../components/common';
import { ROUTES } from '../../config/routes';
import { Notification } from '../../types';

function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 1) return `${days}日前`;
  if (days === 1) return '昨日';
  if (hours > 0) return `${hours}時間前`;
  if (minutes > 0) return `${minutes}分前`;
  return 'たった今';
}

function getNotificationIcon(type: string): React.ReactElement {
  const t = type.toLowerCase();
  if (t.includes('lead') || t.includes('account') || t.includes('user') || t.includes('welcome') || t.includes('register')) {
    return <User className="h-5 w-5" style={{ color: '#0a7a72' }} />;
  }
  if (t.includes('bot') || t.includes('sync') || t.includes('learning') || t.includes('train')) {
    return <RefreshCw className="h-5 w-5" style={{ color: '#0a7a72' }} />;
  }
  if (t.includes('trial') || t.includes('warn') || t.includes('expir') || t.includes('limit')) {
    return <AlertTriangle className="h-5 w-5 text-amber-500" />;
  }
  if (t.includes('chat') || t.includes('message') || t.includes('conversation')) {
    return <MessageSquare className="h-5 w-5 text-blue-500" />;
  }
  if (t.includes('email') || t.includes('verify') || t.includes('confirm') || t.includes('success') || t.includes('publish')) {
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  }
  return <Bell className="h-5 w-5" style={{ color: '#0a7a72' }} />;
}

function getNotificationIconBg(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('trial') || t.includes('warn') || t.includes('expir') || t.includes('limit')) {
    return '#FEF3C7';
  }
  if (t.includes('email') || t.includes('verify') || t.includes('confirm') || t.includes('success') || t.includes('publish')) {
    return '#DCFCE7';
  }
  return '#F0FDFA';
}

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [proLockOpen, setProLockOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const { page: urlPage, limit, setPage: setUrlPage } = usePaginationParams();

  const params: NotificationsParams = {
    page: urlPage,
    limit,
    unread_only: unreadOnly,
    type: typeFilter,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', params],
    queryFn: () => notificationService.getNotifications(params),
  });

  const { data: unreadData } = useQuery({
    queryKey: ['notifications', { page: 1, limit: 1, unread_only: true }],
    queryFn: () => notificationService.getNotifications({ page: 1, limit: 1, unread_only: true }),
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      showToast.success('全ての通知を既読にしました');
    },
  });

  const notifications: Notification[] = data?.data?.notifications || [];
  const pagination = data?.data?.pagination;
  const unreadCount = unreadData?.data?.pagination?.total ?? 0;
  const totalPages = pagination ? Math.ceil(pagination.total / limit) : 0;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.link) {
      if (['trial', 'starter'].includes(user?.plan || '')) {
        setProLockOpen(true);
        return;
      }
      navigate(notification.link);
    }
  };

  return (
    <>
    {proLockOpen && (
      <ProLockOverlay
        variant="modal"
        title={<>この機能は<span style={{ color: '#0a7a72' }}>Proプラン</span>から</>}
        description="Proプランにアップグレードすると、リード一覧の閲覧など全機能がご利用いただけます。"
        onUpgrade={() => { setProLockOpen(false); navigate(ROUTES.SUBSCRIPTION); }}
      />
    )}
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* ヘッダー */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary">通知</h1>
          <p className="text-sm text-gray-500 mt-1">チャットボットの状況やリード獲得をお知らせします。</p>
        </div>
        <button
          type="button"
          onClick={() => markAllAsReadMutation.mutate()}
          disabled={markAllAsReadMutation.isPending}
          className="text-sm hover:underline disabled:opacity-50 mt-1 flex-shrink-0"
          style={{ color: '#0a7a72' }}
        >
          すべて既読にする
        </button>
      </div>

      {/* フィルタ Pill ボタン */}
      <div className="flex flex-wrap gap-2 mb-5 mt-4">
        <button
          type="button"
          data-testid="notification-filter-all"
          onClick={() => { setUnreadOnly(false); setTypeFilter(undefined); setUrlPage(1); }}
          className="px-4 py-1.5 text-sm font-medium transition-colors rounded-[20px]"
          style={
            !unreadOnly && !typeFilter
              ? { backgroundColor: '#0a7a72', color: '#fff', border: 'none' }
              : { backgroundColor: 'transparent', color: '#64748B', border: '1px solid #E2E8F0' }
          }
        >
          すべて
        </button>
        <button
          type="button"
          data-testid="notification-filter-unread"
          onClick={() => { setUnreadOnly(true); setTypeFilter(undefined); setUrlPage(1); }}
          className="px-4 py-1.5 text-sm font-medium transition-colors rounded-[20px]"
          style={
            unreadOnly
              ? { backgroundColor: '#0a7a72', color: '#fff', border: 'none' }
              : { backgroundColor: 'transparent', color: '#64748B', border: '1px solid #E2E8F0' }
          }
        >
          未読{unreadCount > 0 ? ` ${unreadCount}` : ''}
        </button>
      </div>

      {/* 通知リスト */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Bell className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">通知はありません</p>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
          {notifications.map((notification, index) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              data-testid="notification-item"
              className="flex items-start gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
              style={{
                padding: '16px 20px',
                borderBottom: index < notifications.length - 1 ? '1px solid #F1F5F9' : 'none',
                opacity: notification.is_read ? 0.7 : 1,
              }}
            >
              {/* タイプ別アイコン */}
              <div
                className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: getNotificationIconBg(notification.type) }}
              >
                {getNotificationIcon(notification.type)}
              </div>

              {/* 本文 */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm text-text-primary"
                  style={{ fontWeight: notification.is_read ? 400 : 600 }}
                >
                  {notification.title}
                </p>
                <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{notification.message}</p>
              </div>

              {/* タイムスタンプ */}
              <div className="flex-shrink-0 text-right ml-4">
                <span style={{ fontSize: '12px', color: '#94A3B8', whiteSpace: 'nowrap' }}>
                  {getRelativeTime(notification.created_at)}
                </span>
                {!notification.is_read && (
                  <div
                    className="w-2 h-2 rounded-full ml-auto mt-1"
                    style={{ backgroundColor: '#0a7a72' }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ページネーション */}
      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">
            全{pagination.total}件中 {(urlPage - 1) * limit + 1}-{Math.min(urlPage * limit, pagination.total)}件を表示
          </span>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = i + 1;
              const isActive = pageNum === urlPage;
              return (
                <button
                  key={pageNum}
                  onClick={() => setUrlPage(pageNum)}
                  style={{
                    width: '36px', height: '36px', borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: isActive ? 700 : 400,
                    background: isActive ? '#0a7a72' : 'transparent',
                    color: isActive ? '#fff' : '#64748B',
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            {urlPage < totalPages && (
              <button
                onClick={() => setUrlPage(urlPage + 1)}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: '14px', fontWeight: 500, marginLeft: '4px' }}
              >
                次へ
              </button>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};
